import React, { useEffect, useState } from "react";
import { Box, Button, IconButton, Paper, Typography, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import { gateway } from "../gateway";
import { updateSignupEmployeeState } from "../redux/features/signupEmployeeStateSlice";
import { SnackbarAlert } from "./common/SnackbarAlert";
import { LoaderComponent } from "./Loader";
import styled from "@emotion/styled";
import { colors } from "./ui/colors";
import { Close, DragIndicator, AccessTime, WbSunny, DarkMode, WbTwilight, Edit } from "@mui/icons-material";

// --- Interfaces ---
interface ShiftOption {
  id: string;
  time: string;
  icon: React.ReactNode;
}

interface WeeklySchedule {
  [key: string]: string[];
}

interface DayColumnProps {
  isOver?: boolean;
}

const DayColumn = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isWeekend" && prop !== "isOver",
})<DayColumnProps>(({ isOver }) => ({
  flex: 1,
  minWidth: "125px",
  minHeight: "125px",
  backgroundColor: isOver ? "var(--fx-colors-warning-50, #FFF7ED)" : "var(--fx-surface-base, #FFFFFF)",
  borderRight: "1px solid var(--fx-surface-border, #E2E8F0)",
  transition: "all 0.2s ease",
  display: "flex",
  flexDirection: "column",
  "&:last-child": { borderRight: "none" },
}));

const ShiftBadge = styled(Box)<{ time: string }>(({ time }) => {
  let bgColor = "#f8fafc";
  let borderColor = "#e2e8f0";

  if (time.includes("09:00")) {
    bgColor = "#eff6ff";
    borderColor = "#dbeafe";
  }
  if (time.includes("14:00")) {
    bgColor = "#fff7ed";
    borderColor = "#ffedd5";
  }
  if (time.includes("19:00")) {
    bgColor = "#f5f3ff";
    borderColor = "#ede9fe";
  }

  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "3px 8px",
    backgroundColor: bgColor,
    border: `1px solid ${borderColor}`,
    borderRadius: "8px",
    marginBottom: "4px",
    transition: "transform 0.1s ease",

    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    },
  };
});

const DragItem = styled(Box)<{ time: string }>(({ time }) => {
  let borderColor = "#e2e8f0";
  let iconColor = "#64748b";

  if (time.includes("09:00")) {
    borderColor = "#dbeafe";
    iconColor = "#3b82f6";
  }
  if (time.includes("14:00")) {
    borderColor = "#ffedd5";
    iconColor = "#f97316";
  }
  if (time.includes("19:00")) {
    borderColor = "#ede9fe";
    iconColor = "#8b5cf6";
  }

  return {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "4px 8px",
    cursor: "grab",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    border: `1px solid ${borderColor}`,
    borderRadius: "10px",
    fontSize: "0.85rem",
    fontWeight: 600,
    transition: "all 0.2s",
    "& svg": {
      color: iconColor,
      transition: "color 0.2s ease",
    },

    "&:hover": {
      borderColor: colors.orange.base,
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      "& svg": {
        color: colors.orange.base,
      },
    },
    "&:active": { cursor: "grabbing" },
  };
});

export const ConnectBusinessAvailability: React.FC<any> = ({ updateComponentState = updateSignupEmployeeState }) => {
  const tokenLocal = sessionStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [changes, setChanges] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);
  const [backupSchedule, setBackupSchedule] = useState<WeeklySchedule | null>(null);
  const [alert, setAlert] = useState({ open: false, msg: "", severity: "success" as "success" | "error" });

  const [schedule, setSchedule] = useState<WeeklySchedule>({
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  });

  const shiftOptions: ShiftOption[] = [
    { id: "morn", time: "09:00 - 13:00", icon: <WbSunny sx={{ fontSize: 12 }} /> },
    { id: "aft", time: "14:00 - 18:00", icon: <WbTwilight sx={{ fontSize: 12 }} /> },
    { id: "eve", time: "19:00 - 22:00", icon: <DarkMode sx={{ fontSize: 12 }} /> },
  ];

  // --- Handlers ---
  const handleDragStart = (e: React.DragEvent, time: string) => {
    e.dataTransfer.setData("time", time);
  };

  const handleDrop = (e: React.DragEvent, day: string) => {
    e.preventDefault();
    setDragOverDay(null);
    const time = e.dataTransfer.getData("time");

    if (time && !schedule[day].includes(time)) {
      setSchedule((prev) => ({ ...prev, [day]: [...prev[day], time] }));
      setChanges(true);
    }
  };

  const removeShift = (day: string, time: string) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: prev[day].filter((t) => t !== time),
    }));
    setChanges(true);
  };
  const handleCancelEditing = () => {
    if (backupSchedule) {
      setSchedule(backupSchedule);
    }
    setIsEditing(false);
    setChanges(false);
  };

  const handleStartEditing = () => {
    setBackupSchedule(schedule);
    setIsEditing(true);
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      await gateway.put(
        "/availability/self",
        { availability: schedule },
        {
          headers: { Authorization: `${tokenLocal}` },
        },
      );
      setAlert({ open: true, msg: "Disponibilidad guardada", severity: "success" });
      setChanges(false);
    } catch (err) {
      setAlert({ open: true, msg: "Error al guardar", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const hasAnyShift = Object.values(schedule).some((dayShifts) => dayShifts.length > 0);

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid var(--fx-surface-border, #E2E8F0)", overflow: "hidden" }}>
        <Box sx={{ p: 2, pb: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Avalability
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Delete All*/}
            {isEditing && hasAnyShift && (
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setSchedule({ Lunes: [], Martes: [], Miércoles: [], Jueves: [], Viernes: [], Sábado: [], Domingo: [] });
                  setChanges(true);
                }}
                sx={{ textTransform: "none", fontWeight: 700, border: "1px dashed var(--fx-surface-border, #E2E8F0)" }}
              >
                Remove all
              </Button>
            )}

            {/* Edit / Cancel */}
            {isEditing ? (
              <Button
                variant="outlined"
                onClick={handleCancelEditing}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                  color: "var(--fx-surface-textsecondary, #64748B)",
                  "&:hover": { borderColor: "var(--fx-surface-texttertiary, #94A3B8)", bgcolor: "var(--fx-surface-sunken, #F8FAFC)" },
                }}
              >
                Cancel
              </Button>
            ) : (
              <IconButton
                onClick={handleStartEditing}
                aria-label="edit"
                sx={{
                  color: colors.orange.base,
                  border: `1px solid ${colors.orange.base}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: "#e67e00",
                    bgcolor: "rgba(255, 140, 0, 0.04)",
                  },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}

            {/* Save */}
            {changes && isEditing && (
              <Button
                variant="contained"
                onClick={async () => {
                  await handleSave();
                  setIsEditing(false);
                }}
                sx={{ bgcolor: colors.orange.base, textTransform: "none", borderRadius: "8px" }}
              >
                Save
              </Button>
            )}
          </Box>
        </Box>

        {isEditing && (
          <Box sx={{ px: 3, pb: 2, display: "flex", gap: 1.5, alignItems: "center", animation: "fadeIn 0.3s" }}>
            <DragIndicator sx={{ color: "#cbd5e1", fontSize: 18 }} />
            {shiftOptions.map((opt) => (
              <DragItem time={opt.time} key={opt.id} draggable onDragStart={(e) => handleDragStart(e, opt.time)}>
                {React.cloneElement(opt.icon as React.ReactElement, {
                  sx: { fontSize: 14, color: "var(--fx-surface-textsecondary, #64748B)" },
                })}
                {opt.time}
              </DragItem>
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", border: "1px solid var(--fx-surface-border, #E2E8F0)", overflowX: "auto" }}>
          {Object.keys(schedule).map((day) => (
            <DayColumn
              key={day}
              isOver={dragOverDay === day}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverDay(day);
              }}
              onDragLeave={() => setDragOverDay(null)}
              onDrop={(e) => handleDrop(e, day)}
            >
              <Box sx={{ textAlign: "center", borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }}>
                <Typography variant="caption" color={colors.orange.base} fontWeight={800}>
                  {day}
                </Typography>
              </Box>
              <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: "2px" }}>
                {schedule[day].map((time, idx) => (
                  <ShiftBadge key={`${day}-${time}-${idx}`} time={time}>
                    <Typography className="time-text" sx={{ fontSize: "0.65rem", fontWeight: 800 }}>
                      {time.replace(" - ", "-")}
                    </Typography>

                    {isEditing && (
                      <IconButton
                        size="small"
                        onClick={() => removeShift(day, time)}
                        sx={{ p: 0, opacity: 0.6, "&:hover": { opacity: 1, color: "error.main" } }}
                      >
                        <Close sx={{ fontSize: 12 }} />
                      </IconButton>
                    )}
                  </ShiftBadge>
                ))}

                {schedule[day].length === 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "35px",
                      borderRadius: "8px",
                      border: isEditing ? "1px dashed var(--fx-surface-border, #E2E8F0)" : "none",
                      bgcolor: isEditing ? "rgba(241, 245, 249, 0.5)" : "transparent",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        color: "var(--fx-surface-texttertiary, #94A3B8)",
                        textAlign: "center",
                        px: 1,
                        lineHeight: 1.1,
                      }}
                    >
                      {isEditing ? "Drop here" : "No shifts"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DayColumn>
          ))}
        </Box>
      </Paper>

      <LoaderComponent open={loading} />
      <SnackbarAlert
        openState={alert.open}
        setOpenState={(val: boolean) => setAlert((prev) => ({ ...prev, open: val }))}
        severity={alert.severity}
        message={alert.msg}
      />
    </Box>
  );
};
